// courier-balances.state.ts
import {
  State, Action, StateContext, Selector,
} from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { CourierBalanceService } from '@core/services/courier-balance.service';
import { Observable, tap } from 'rxjs';
import {
  BasePaginatedEntityListStateWithDateFilter,
  BaseSortedEntityListStateWithDateFilter,
} from '@core/interfaces/base-entity-list-state.interface';
import {
  CourierBalanceFilter,
  CourierBalanceGqlType,
  CouriersBalancesListResponse,
  EncashmentTransaction,
  EncashmentTransactionsListAdminResponse,
} from '@core/interfaces/courier-balance.interface';
import {
  getDefaultPaginatedEntityListStateWithDateFilter,
  getDefaultSortedEntityListStateWithDateFilter,
} from '@core/helper/state-helper';
import { gateDateRangeFromNgbDateRange } from '@core/helper/date-helper';
import { NgbDateRange, DateRange } from '@core/interfaces/date-filter.interface';
import { OffsetPagination } from '@core/interfaces/pagination-params.interface';
import { ApolloQueryResult } from '@apollo/client/core';
import { CourierBalancesTypeEnum } from '@core/enums/courier-balances.enum';
import { pickBy, isNil } from 'lodash-es';
import { SortInterface } from '@core/interfaces/get-list.interface';
import { SortEnum } from '@core/enums/sort.enum';
import {
  GetCouriersTotalBalance,
  GetListCourierBalance,
  GetListEncashmentTransactions,
  SetCourierBalanceActiveTab,
  SetTransactionsFilter,
  SetCourierBalanceDateFilter,
  SetTransactionsPagination,
  SetEncashmentTransactionsSort,
  SetCourierBalanceSort,
} from '../action/courier-balance.action';

export interface CourierBalancesStateModel {
  encashmentTransactions: BaseSortedEntityListStateWithDateFilter<EncashmentTransaction, CourierBalanceFilter>;
  courierBalance: BaseSortedEntityListStateWithDateFilter<CourierBalanceGqlType, CourierBalanceFilter>;
  currentTab: CourierBalancesTypeEnum;
  couriersTotalBalance: number | null;
}

@State<CourierBalancesStateModel>({
  name: 'courierBalances',
  defaults: {
    encashmentTransactions: {
      ...getDefaultSortedEntityListStateWithDateFilter<CourierBalanceFilter, EncashmentTransaction>(),
    },
    courierBalance: {
      ...getDefaultSortedEntityListStateWithDateFilter<CourierBalanceFilter, CourierBalanceGqlType>(),
    },
    currentTab: CourierBalancesTypeEnum.general,
    couriersTotalBalance: null,
  },
})
@Injectable()
export class CourierBalancesState {
  constructor(
    private apollo: Apollo,
    private courierBalanceService: CourierBalanceService,
  ) {}

  @Selector()
  public static getListEncashmentTransactions(state: CourierBalancesStateModel): EncashmentTransaction[] {
    return state.encashmentTransactions.list;
  }

  @Selector()
  public static getCourierBalanceList(state: CourierBalancesStateModel): CourierBalanceGqlType[] {
    return state.courierBalance.list;
  }

  @Selector()
  public static getCourierBalanceTotal(state: CourierBalancesStateModel): number | null {
    return state.couriersTotalBalance;
  }

  @Selector()
  public static getEncashmentTransactionSort(state: CourierBalancesStateModel): SortInterface {
    return state.encashmentTransactions.sort;
  }

  @Selector()
  public static getCourierBlancesSort(state: CourierBalancesStateModel): SortInterface {
    return state.courierBalance.sort;
  }

  @Selector()
  public static getCourierCurrentTab(state: CourierBalancesStateModel): string {
    return state.currentTab;
  }

  @Selector()
  public static getTransactionsTotal(state: CourierBalancesStateModel): number {
    return state.encashmentTransactions.total;
  }

  @Selector()
  public static getBalancesTotal(state: CourierBalancesStateModel): number {
    return state.courierBalance.total;
  }

  @Selector()
  static getRawDateFilter(state: CourierBalancesStateModel): NgbDateRange {
    return state.encashmentTransactions.rawDateFilter;
  }

  @Selector()
  static getDateFilter(state: CourierBalancesStateModel): DateRange {
    return gateDateRangeFromNgbDateRange(state.encashmentTransactions.rawDateFilter);
  }

  @Selector()
  static getCourierBalanceFilter(state: CourierBalancesStateModel): CourierBalanceFilter {
    return state.encashmentTransactions.filter;
  }

  @Selector()
  static getEncashmentTransactionsPaginationParams(state: CourierBalancesStateModel): OffsetPagination {
    return state.encashmentTransactions.pagination;
  }

  @Selector()
  static getSelectedTab(state: CourierBalancesStateModel): CourierBalancesTypeEnum {
    return state.currentTab;
  }

  @Action(GetListEncashmentTransactions)
  public getListEncashmentTransactions(
    ctx: StateContext<CourierBalancesStateModel>,
    { params }: GetListEncashmentTransactions,
  ): Observable<ApolloQueryResult<EncashmentTransactionsListAdminResponse>> {
    return this.courierBalanceService.getEncashmentTransactions(params!).pipe(
      tap((res) => {
        const currentState = ctx.getState();
        ctx.patchState({
          encashmentTransactions: {
            ...currentState.encashmentTransactions,
            list: res.data.encashmentTransactionsListAdmin.result,
            total: res.data.encashmentTransactionsListAdmin.total,
          },
        });
      }),
    );
  }

  @Action(SetEncashmentTransactionsSort)
  public setEncashmentTransactionsSort(
    ctx: StateContext<CourierBalancesStateModel>,
    { sort }: SetEncashmentTransactionsSort,
  ): void {
    const currentState = ctx.getState();
    ctx.patchState({
      encashmentTransactions: {
        ...currentState.encashmentTransactions,
        sort,
      },
    });
  }

  @Action(SetCourierBalanceSort)
  public setCourierBalanceSort(ctx: StateContext<CourierBalancesStateModel>, { sort }: SetCourierBalanceSort): void {
    const currentState = ctx.getState();
    ctx.patchState({
      courierBalance: {
        ...currentState.courierBalance,
        sort,
      },
    });
  }

  @Action(GetListCourierBalance)
  public getListCourierBalance(
    ctx: StateContext<CourierBalancesStateModel>,
    { params }: GetListCourierBalance,
  ): Observable<ApolloQueryResult<CouriersBalancesListResponse>> {
    const safeParams = {
      ...params,
      filter: { ...params?.filter },
    };

    if ('type' in safeParams.filter) {
      delete safeParams.filter.type;
    }

    return this.courierBalanceService.getCouriersBalancesList(safeParams!).pipe(
      tap((res) => {
        const currentState = ctx.getState();
        ctx.patchState({
          courierBalance: {
            ...currentState.courierBalance,
            list: res.data.couriersBalanceList.result,
            total: res.data.couriersBalanceList.total,
          },
        });
      }),
    );
  }

  @Action(SetTransactionsFilter)
  public setEncashmentTransactionsFilter(
    ctx: StateContext<CourierBalancesStateModel>,
    { filter }: SetTransactionsFilter,
  ): void {
    const currentState = ctx.getState();
    const updatedFilter = {
      ...currentState.encashmentTransactions.filter,
      ...filter,
    };
    const filterToApply = pickBy(updatedFilter, (v) => !isNil(v));
    ctx.patchState({
      encashmentTransactions: {
        ...currentState.encashmentTransactions,
        filter: filterToApply,
      },
    });
  }

  @Action(SetCourierBalanceActiveTab)
  public setCourierBalanceActiveTab(
    ctx: StateContext<CourierBalancesStateModel>,
    { tab }: SetCourierBalanceActiveTab,
  ): void {
    ctx.patchState({
      currentTab: tab,
    });
  }

  @Action(SetTransactionsPagination)
  public setCourierBalancePagination(
    ctx: StateContext<CourierBalancesStateModel>,
    { paginationParams }: SetTransactionsPagination,
  ): void {
    const currentState = ctx.getState();
    ctx.patchState({
      encashmentTransactions: {
        ...currentState.encashmentTransactions,
        pagination: paginationParams,
      },
    });
  }

  @Action(SetCourierBalanceDateFilter)
  public setCourierBalanceDateFilter(
    ctx: StateContext<CourierBalancesStateModel>,
    { dateFilter }: SetCourierBalanceDateFilter,
  ): void {
    const currentState = ctx.getState();
    ctx.patchState({
      encashmentTransactions: {
        ...currentState.encashmentTransactions,
        rawDateFilter: {
          fromDate: dateFilter.fromDate,
          toDate: dateFilter.toDate,
        },
      },
    });
    ctx.patchState({
      courierBalance: {
        ...currentState.courierBalance,
        rawDateFilter: {
          fromDate: dateFilter.fromDate,
          toDate: dateFilter.toDate,
        },
      },
    });
  }

  @Action(GetCouriersTotalBalance)
  public loadTotalBalance(
    ctx: StateContext<CourierBalancesStateModel>,
    { filter }: GetCouriersTotalBalance,
  ): Observable<ApolloQueryResult<{ couriersTotalBalance: number }>> {
    return this.courierBalanceService.getCouriersTotalBalance(filter).pipe(
      tap((res) => {
        ctx.patchState({
          couriersTotalBalance: res.data.couriersTotalBalance,
        });
      }),
    );
  }
}
